import React from 'react';
import { useRouter } from 'next/router';
import type { Route } from 'nextjs-routes';
import { makeAuthProvider } from '@zg-rentals/auth-react';
import { LoginMode, zillowWebLogin } from '@zillow/auth-core';
import { RentCreditReportWebApiHandler_get } from '@zg-rentals/ts-rent-credit-report-client';
import { ReportingForAllEnrollmentStatus } from '@zg-rentals/ts-rent-credit-report-client';
import type { UsersGetResponseDto } from '@zg-rentals/ts-rent-credit-report-client';
import { useRentCreditReportWebApiHandler_enrollmentUpdate } from '@zg-rentals/ts-rent-credit-report-client-react-query';
import { createClient } from '@zg-rentals/http-client';

export type RentReportingUser = UsersGetResponseDto;

const authCheckClient = createClient({
  logging: false,
  monitoring: true,
  credentials: 'include',
  prefixUrl: process.env.NEXT_PUBLIC_PREFIX_URL,
  redirect: 'error',
  timeout: 60 * 1000,
});

export type EnhancedRentReportingUser = RentReportingUser & {
  isLoggedIn: boolean;
  hasInitiatedEnrollment: boolean;
  hasCompletedEnrollment: boolean;
};

export const {
  Provider: RentReportingAuthProvider,
  Boundary: RentReportingAuthBoundary,
  useAuth,
} = makeAuthProvider({
  fetchUser: async () => {
    try {
      // Use authCheckClient which suppresses error logging since it's expected when not logged in
      const response = await RentCreditReportWebApiHandler_get(authCheckClient);
      return response;
    } catch (error) {
      // Treat all API errors as if the user is not logged in
      // Empty encodedZuid indicates not logged in
      return {
        encodedZuid: '',
        enrollmentStatus: ReportingForAllEnrollmentStatus.NOT_ENROLLED,
        // currently, this value isn't used for any business logic - revisit once we begin using it
        completedEnrollmentTimestamp: 0,
      };
    }
  },

  authorizeUser: (user: RentReportingUser | null) => {
    // User is null means still loading, not unauthorized
    if (!user) {
      return false;
    }

    // User is authorized if they have an encodedZuid
    return !!user.encodedZuid;
  },

  // Custom query key for this auth provider
  queryKey: ['rent-reporting', 'auth', 'user'],

  providerQueryOptions: {
    retry: false, // Don't retry on 401 errors
  },
});

export const useRentReportingAuth = () => {
  const { user: rawUser, refetch } = useAuth();
  const baseUser = rawUser as RentReportingUser | null;
  const router = useRouter();

  const enrollmentMutation = useRentCreditReportWebApiHandler_enrollmentUpdate({
    onSuccess: async () => {
      await refetch();
    },
  });

  const getRedirectUrl = (
    isDevelopment: boolean,
    currentPath: string,
    searchParams: URLSearchParams,
    postLoginAction: boolean,
  ) => {
    if (isDevelopment) {
      return postLoginAction
        ? `${currentPath}/esusu?${searchParams.toString()}`
        : `${currentPath}?${searchParams.toString()}`;
    }
    return postLoginAction ? `/enroll/esusu?${searchParams.toString()}` : `?${searchParams.toString()}`;
  };

  const login = (authTriggerSource?: string, postLoginAction?: string) => {
    if (typeof window !== 'undefined') {
      const isDevelopment = process.env.NODE_ENV === 'development';
      const currentUrl = new URL(window.location.href);

      if (authTriggerSource) {
        currentUrl.searchParams.set('auth_trigger', authTriggerSource);
      }

      if (postLoginAction) {
        currentUrl.searchParams.set('post_login_action', postLoginAction);
      }
      const redirectUrl = getRedirectUrl(
        isDevelopment,
        currentUrl.pathname,
        currentUrl.searchParams,
        !!postLoginAction,
      );

      zillowWebLogin({
        loginMode: isDevelopment ? LoginMode.REDIRECT : LoginMode.POPUP,
        redirectUri: isDevelopment
          ? `/rental/rent-credit-report/forward?${new URLSearchParams({
              port: '3000',
              protocol: 'https',
              path: redirectUrl,
            })}`
          : undefined,
        trackingConfig: {
          entryPoint: 'rent-reporting-web',
        },
        envConfig: {
          baseURLOverride: isDevelopment ? 'https://www.qa.zillow.net' : undefined,
        },
        onSuccessfulLogin: isDevelopment
          ? undefined
          : () => {
              router.push(redirectUrl as Route['pathname']);
            },
      });
    }
  };

  // Create enhanced user object with derived state properties
  const user: EnhancedRentReportingUser | null = baseUser
    ? {
        ...baseUser,
        isLoggedIn: !!baseUser.encodedZuid,
        hasInitiatedEnrollment: baseUser.enrollmentStatus === ReportingForAllEnrollmentStatus.INITIATED_ENROLLMENT,
        hasCompletedEnrollment: baseUser.enrollmentStatus === ReportingForAllEnrollmentStatus.COMPLETED_ENROLLMENT,
      }
    : null;

  return {
    user,
    login,
    enrollmentMutation,
  };
};

// Helper function to determine if user should be redirected based on current route and user
export const getRedirectPath = (
  currentPath: string,
  queryParams: Record<string, string>,
  user: EnhancedRentReportingUser | null,
) => {
  const isLoggedIn = user?.isLoggedIn ?? false;
  const hasInitiatedEnrollment = user?.hasInitiatedEnrollment ?? false;
  const hasCompletedEnrollment = user?.hasCompletedEnrollment ?? false;

  const shouldAllowDashboardAccess = queryParams.enrollmentCompleted === 'true';
  const shouldAllowEnrollAccess = queryParams.ignoreEnrollment === 'true';

  const isDashboardPath = currentPath === '/' || currentPath === '/renting/your-rental/rent-reporting';

  // 1. User is not logged in and has not initiated enrollment
  if (!isLoggedIn && !hasInitiatedEnrollment) {
    if (isDashboardPath) {
      // 1a.i - prompt for login (handled by Boundary)
      return null;
    }
    if (currentPath === '/enroll') {
      // 1b.i - do not prompt for login
      return null;
    }
    if (currentPath === '/enroll/esusu') {
      // 1c.i - redirect back to /enroll
      return '/enroll';
    }
  }

  // 2. User is logged in and has not initiated enrollment
  if (isLoggedIn && !hasInitiatedEnrollment && !hasCompletedEnrollment) {
    if (isDashboardPath) {
      // 2a.i - redirect to /enroll
      return '/enroll';
    }
    if (currentPath === '/enroll') {
      // 2b.i - allow user to continue through flow
      return null;
    }
    if (currentPath === '/enroll/esusu') {
      // 2c.i - redirect back to /enroll
      return '/enroll';
    }
  }

  // 3. User is logged in and has initiated enrollment
  if (isLoggedIn && hasInitiatedEnrollment && !hasCompletedEnrollment) {
    if (isDashboardPath && !shouldAllowDashboardAccess) {
      // 3a.i - redirect to /esusu to continue enrollment
      return '/enroll/esusu';
    }
    if (currentPath === '/enroll' && !shouldAllowEnrollAccess) {
      // 3b.i - redirect to /esusu to continue enrollment
      return '/enroll/esusu';
    }
    if (currentPath === '/enroll/esusu') {
      // 3c.i - allow user to continue through flow
      return null;
    }
  }

  // 4. User is logged in and has completed enrollment
  if (isLoggedIn && hasCompletedEnrollment) {
    if (isDashboardPath) {
      // 4a.i - no redirect action taken
      return null;
    }
    if (currentPath === '/enroll') {
      // 4b.i - redirect to ReportingDashboard
      return '/';
    }
    if (currentPath === '/enroll/esusu') {
      // 4c.i - redirect to ReportingDashboard
      return '/';
    }
  }

  return null;
};
